import React, { useState, useEffect, useCallback } from 'react';

/**
 * RaceRunPerfPanel — content-only diagnostic window (`raceRunPerf`, M9 D1d).
 *
 * Renders the latest `make race-load-test` run from
 * GET /api/diag/race/load-latest (HTTP poll @5s — no broadcasts, so no
 * DIRECT_RELAY_TYPES involvement): finish/DNF/error counts, avg/min/max
 * times, per-participant table, the G-D2 msgs/sec instrument, tick verdict,
 * and a click-to-load run history. GM_RACE_LOAD_PERF_SPEC §4.
 */

interface LoadParticipant {
  heroName: string;
  sessionId: string;
  status: string;
  place: number | null;
  finishTimeMs: number | null;
  checkpoints: number;
  error?: string;
}

interface LoadSummary {
  ok: boolean;
  runId: string;
  runStartAt: string;
  clientCount: number;
  finishedCount: number;
  dnfCount: number;
  disconnectedCount: number;
  errorCount: number;
  averageFinishTimeMs: number | null;
  minFinishTimeMs: number | null;
  maxFinishTimeMs: number | null;
  messagesPerSecond?: number | null;
  messagesPerSecondPerClient?: number | null;
  participants: LoadParticipant[];
  diagSnapshot?: { tickBudget?: { verdict?: { severity?: string } } };
}

interface LoadLatestResponse {
  hasRuns: boolean;
  latest: LoadSummary | null;
  runs: string[];
  requestedRun: string | null;
}

const POLL_MS = 5000;

function ageLabel(runStartAt: string | undefined): string {
  if (!runStartAt) return '';
  const ageSec = Math.max(0, Math.round((Date.now() - Date.parse(runStartAt)) / 1000));
  if (ageSec < 90) return `${ageSec}s ago`;
  if (ageSec < 5400) return `${Math.round(ageSec / 60)}m ago`;
  return `${Math.round(ageSec / 3600)}h ago`;
}

const cell: React.CSSProperties = { padding: '2px 6px', borderBottom: '1px solid rgba(255,255,255,0.08)' };

export const RaceRunPerfPanelContent: React.FC = () => {
  const [data, setData] = useState<LoadLatestResponse | null>(null);
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const load = useCallback(async (run: string | null) => {
    try {
      const url = run
        ? `/api/diag/race/load-latest?run=${encodeURIComponent(run)}`
        : '/api/diag/race/load-latest';
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setFetchError(null);
    } catch (err: any) {
      setFetchError(err?.message ?? String(err));
    }
  }, []);

  useEffect(() => {
    load(selectedRun);
    const interval = setInterval(() => load(selectedRun), POLL_MS);
    return () => clearInterval(interval);
  }, [load, selectedRun]);

  if (fetchError) {
    return <div style={{ padding: 12, color: '#ff6666', fontSize: 12 }}>❌ {fetchError}</div>;
  }
  if (!data) {
    return <div style={{ padding: 12, color: '#aaa', fontSize: 12 }}>Loading…</div>;
  }
  if (!data.hasRuns || !data.latest) {
    return (
      <div style={{ padding: 12, color: '#aaa', fontSize: 12 }}>
        No runs yet — <code style={{ color: '#ffd700' }}>make race-load-test</code> (memory stack up first).
      </div>
    );
  }

  const s = data.latest;
  const tickSeverity = s.diagSnapshot?.tickBudget?.verdict?.severity;

  return (
    <div style={{ padding: 10, color: 'white', overflow: 'auto', height: '100%', fontSize: 12 }}>
      {/* Header: run + ok badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{
          padding: '2px 8px', borderRadius: 4, fontWeight: 700,
          background: s.ok ? 'rgba(0,200,80,0.25)' : 'rgba(220,50,50,0.25)',
          color: s.ok ? '#4dff88' : '#ff6666',
        }}>{s.ok ? 'OK' : 'FAIL'}</span>
        <span style={{ color: '#ffd700' }}>{s.runId}</span>
        <span style={{ color: '#888' }}>{ageLabel(s.runStartAt)}</span>
      </div>

      {/* Counts + times */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 8 }}>
        <div><strong>Finished:</strong> {s.finishedCount}/{s.clientCount}</div>
        <div style={{ color: s.dnfCount > 0 ? '#ffaa44' : undefined }}><strong>DNF:</strong> {s.dnfCount}</div>
        <div style={{ color: s.disconnectedCount > 0 ? '#ff6666' : undefined }}>
          <strong>Disconnected:</strong> {s.disconnectedCount}
        </div>
        <div style={{ color: s.errorCount > 0 ? '#ff6666' : undefined }}><strong>Errors:</strong> {s.errorCount}</div>
        <div><strong>Avg finish:</strong> {s.averageFinishTimeMs ?? '—'}ms</div>
        <div><strong>Min/Max:</strong> {s.minFinishTimeMs ?? '—'} / {s.maxFinishTimeMs ?? '—'}ms</div>
      </div>

      {/* Perf strip — the G-D2 instrument */}
      <div style={{
        padding: 6, marginBottom: 8, borderRadius: 4,
        background: 'rgba(80,140,255,0.12)', border: '1px solid rgba(80,140,255,0.35)',
      }}>
        <strong>msgs/sec:</strong> {s.messagesPerSecond ?? '—'} total
        {' · '}{s.messagesPerSecondPerClient ?? '—'}/client
        {tickSeverity && (
          <span style={{ marginLeft: 8, color: tickSeverity === 'severe' ? '#ff6666' : tickSeverity === 'mild' ? '#ffaa44' : '#4dff88' }}>
            tick: {tickSeverity}
          </span>
        )}
      </div>

      {/* Participants */}
      <div style={{ maxHeight: 190, overflowY: 'auto', marginBottom: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ color: '#ffd700', textAlign: 'left' }}>
              <th style={cell}>racer</th><th style={cell}>status</th><th style={cell}>place</th>
              <th style={cell}>time</th><th style={cell}>cp</th>
            </tr>
          </thead>
          <tbody>
            {s.participants.map((p) => (
              <tr key={p.sessionId} style={{ color: p.error ? '#ff6666' : undefined }} title={p.error}>
                <td style={cell}>{p.heroName}</td>
                <td style={cell}>{p.status}</td>
                <td style={cell}>{p.place ?? '—'}</td>
                <td style={cell}>{p.finishTimeMs != null ? `${p.finishTimeMs}ms` : '—'}</td>
                <td style={cell}>{p.checkpoints}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* History */}
      {data.runs.length > 1 && (
        <div>
          <div style={{ color: '#ffd700', marginBottom: 4 }}>History</div>
          <div style={{ maxHeight: 90, overflowY: 'auto' }}>
            {data.runs.map((runId) => (
              <div
                key={runId}
                onClick={() => setSelectedRun(runId === selectedRun ? null : runId)}
                style={{
                  cursor: 'pointer', padding: '2px 6px', borderRadius: 3,
                  background: runId === selectedRun ? 'rgba(255,215,0,0.15)' : undefined,
                  color: runId === selectedRun ? '#ffd700' : '#aaa',
                }}
              >
                {runId}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
